import { getSupportTool } from './common/constants';
import {
    RejectBillingPayrollForm,
    RejectBillingPayrollHistoryDetails,
} from './common/RejectBillingPayrollForm';
import {
    MoveTimesheetToContractForm,
    MoveTimesheetToContractHistoryDetails,
} from './common/MoveTimesheetToContractForm';
import {
    ChangeJobDateForm,
    ChangeJobDateHistoryDetails,
} from './common/ChangeJobDateForm';
import {
    AddChargesToTimesheetForm,
    AddChargesToTimesheetHistoryDetails,
} from './common/AddChargesToTimesheetForm';
import {
    RemoveChargesForm,
    RemoveChargesHistoryDetails,
} from './common/RemoveChargesForm';
import {
    SyncTimesheetContractBillingForm,
    SyncTimesheetContractBillingHistoryDetails,
} from './common/SyncTimesheetContractBillingForm';
import {
    AddJobCityForm,
    AddJobCityHistoryDetails,
} from './common/AddJobCityForm';
import {
    ImportCustomerDataForm,
    ImportCustomerDataHistoryDetails,
} from './common/ImportCustomerDataForm';
import {
    RollbackPayrollExportForm,
    RollbackPayrollExportHistoryDetails,
} from './common/RollbackPayrollExportForm';
import {
    CrewEtaTestDriveForm,
    CrewEtaTestDriveHistoryDetails,
} from './common/CrewEtaTestDriveForm';
import { TestMessagingForm } from './common/TestMessagingForm';
import { MergeProjectHistoryForm, MergeProjects } from './MergeProjects';

const SupportToolRegistry = [
    {
        ...getSupportTool('reject-billing-payroll'),
        adminToolId: 6,
        FormComponent: RejectBillingPayrollForm,
        HistoryDetailsComponent: RejectBillingPayrollHistoryDetails,
        examples: [
            {
                title: 'Incorrect Hours Approved',
                scenario: 'A timesheet with 10 hours was approved, but the actual hours were 8. The customer noticed the billing discrepancy.',
                steps: [
                    'Search for the timesheet number (e.g., "TS-12345")',
                    'Select the timesheet from the dropdown',
                    'Click Submit to remove billing and payroll',
                    'Go to the timesheet and correct the hours',
                    'Re-approve the timesheet to recalculate billing',
                ],
            },
            {
                title: 'Rate Change After Approval',
                scenario: 'The billing rate for a charge type was updated, but timesheets were already approved at the old rate.',
                steps: [
                    'Search for the affected timesheet',
                    'Submit to reject billing/payroll',
                    'The timesheet will return to "Pending Billing" status',
                    'Re-approve to apply the new rate',
                ],
            },
        ],
        warnings: [
            'This action will remove ALL billing and payroll calculations for the selected timesheet.',
            'The timesheet will need to be re-approved to generate new billing and payroll records.',
            'If payroll has already been exported, coordinate with the payroll team before using this tool.',
        ],
        tips: [
            'You can search by timesheet number or partial number.',
            'Check the Execution History below to see previous uses of this tool.',
            'After rejection, the timesheet status will change to "Pending Billing".',
        ],
    },
    {
        ...getSupportTool('merge-project'),
        adminToolId: 17,
        FormComponent: MergeProjects,
        HistoryDetailsComponent: MergeProjectHistoryForm,
        examples: [
            {
                title: 'Move Project to Correct Contract',
                scenario: 'A project was accidentally created under Contract A, but the work was for Contract B.',
                steps: [
                    'Search and select the contract the project is currently under',
                    'Select the project you wish to move',
                    'Search and select the destination contract',
                    'Select Move This Project',
                    'Click Submit to move the project',
                    'Verify the project now appears under the correct contract',
                ],
            },
            {
                title: 'Merge Two Projects in the Same Contract',
                scenario: 'A new project was accidentally created and needs to be merged with an existing one.',
                steps: [
                    'Search and select the contract the project is currently under',
                    'Select the project you wish to remove',
                    'Search and select the same contract the project is currently under',
                    'Select the project you wish to merge with',
                    'Click Submit to merge the projects',
                    'Verify the destination project contains all jobs',
                ],
            },
            {
                title: 'Merge Two Projects in Different Contracts',
                scenario: 'A new project was accidentally created in the wrong contract and needs to be merged with a different one.',
                steps: [
                    'Search and select the contract the project is currently under',
                    'Select the project you wish to move',
                    'Search and select the destination contract',
                    'Select the project you wish to merge with',
                    'Click Submit to merge the projects',
                    'Verify the destination project contains all jobs',
                ],
            },
        ],
        warnings: [
            'Moving a project will update its contract association permanently as well as the association of its jobs and timesheets.',
            'You cannot move or merge a project if it has already been invoiced.',
            'Moving a project to a different contract and then moving it back will not restore the original project number.',
        ],
        tips: [
            'You can delete a project invoice if you need to move an already-invoiced project.',
        ],
    },
    {
        ...getSupportTool('move-timesheet-to-contract'),
        adminToolId: 10,
        FormComponent: MoveTimesheetToContractForm,
        HistoryDetailsComponent: MoveTimesheetToContractHistoryDetails,
        examples: [
            {
                title: 'Move Timesheet to Correct Contract',
                scenario: 'A timesheet was accidentally created under Contract A, but the work was for Contract B.',
                steps: [
                    'Search and select the timesheet(s) to move',
                    'Search and select the destination contract',
                    'Click Submit to move the timesheet',
                    'Verify the timesheet now appears under the correct contract',
                ],
            },
            {
                title: 'Move with Charge Replacement',
                scenario: 'Moving a timesheet to a different contract and replacing the "Regular Hours" charge with a new rate.',
                steps: [
                    'Select the timesheet(s) to move',
                    'Select the destination contract',
                    'Click "Add or Replace Charge"',
                    'Select "Regular Hours" as the base charge',
                    'Select the new charge type to apply',
                    'Check "Replace Charges" if you want to replace instead of add',
                    'Submit to move and update charges',
                ],
            },
            {
                title: 'Move to a Contract That Uses Projects',
                scenario: 'The destination contract uses projects, so the timesheet needs a project to land in.',
                steps: [
                    'Select the timesheet(s) to move',
                    'Select the destination contract',
                    'Pick the project the work belongs to, or choose "+ Create New Project"',
                    'Submit — a new project is built from the timesheet\'s existing job details',
                ],
            },
            {
                title: 'Move to Subcontractor',
                scenario: 'Work was performed by a subcontractor and needs to be billed accordingly.',
                steps: [
                    'Select the timesheet(s)',
                    'Select the destination contract',
                    'Search and select the subcontractor company',
                    'Submit to associate the timesheet with the subcontractor',
                ],
            },
        ],
        warnings: [
            'Moving a timesheet will update its contract association permanently.',
            'If billing has already been approved, you may need to reject billing first.',
            'Charge replacements cannot be undone - verify the correct charge types before submitting.',
            'If the move empties the source project, the rest of that project moves across with it and the emptied project is retired.',
        ],
        tips: [
            'You can select multiple timesheets to move at once.',
            'Use "Add or Replace Charge" to modify charges during the move.',
            'The subcontractor field is optional - only use it if the work was subcontracted.',
            'Timesheets can move between project and non-project contracts in either direction.',
        ],
    },
    {
        ...getSupportTool('change-job-date-time'),
        adminToolId: 3,
        FormComponent: ChangeJobDateForm,
        HistoryDetailsComponent: ChangeJobDateHistoryDetails,
        examples: [
            {
                title: 'Timesheet on Wrong Date',
                scenario: 'A timesheet was created for the wrong date and needs to be moved to the correct day.',
                steps: [
                    'Search for and select the timesheet number',
                    'Wait for the invoice check to complete',
                    'Select the correct date',
                    'Click Submit to apply the change',
                ],
            },
            {
                title: 'Change Start Time',
                scenario: 'The start time on a timesheet needs to be corrected.',
                steps: [
                    'Select the timesheet',
                    'Check "Change start time"',
                    'Enter the correct start time',
                    'Click Submit',
                ],
            },
        ],
        warnings: [
            'Timesheets that are Approved, Exported, or Deleted cannot be moved.',
            'The new date must be after the last invoiced date for the project.',
            'When moving a rental timesheet, the Rental Start/End Date will not change.',
        ],
        tips: [
            'The date field is disabled until a timesheet is selected and checked for invoices.',
            'You can change the date, the start time, or both at the same time.',
            'Check the Execution History below to review previous uses of this tool.',
        ],
    },
    {
        ...getSupportTool('add-charges-to-timesheet'),
        adminToolId: 4,
        FormComponent: AddChargesToTimesheetForm,
        HistoryDetailsComponent: AddChargesToTimesheetHistoryDetails,
        examples: [
            {
                title: 'Add a Surcharge to a Timesheet',
                scenario: 'A customer contract requires a fuel surcharge to be added based on the labor charge.',
                steps: [
                    'Select the timesheet number(s)',
                    'Click "Add/Replace Charges"',
                    'Select the base charge type and the charge to add',
                    'Click Add, then Submit',
                ],
            },
            {
                title: 'Replace a Charge Type',
                scenario: 'An incorrect charge type was applied and needs to be replaced with the correct one.',
                steps: [
                    'Select the timesheet',
                    'Click "Add/Replace Charges"',
                    'Select the charge to base it on, then select the replacement charge',
                    'Check "Replace Charges" to replace instead of add alongside',
                    'Click Add, then Submit',
                ],
            },
        ],
        warnings: [
            'Adding charges to an approved timesheet may affect billing calculations.',
            'If billing has already been processed, use Reject Billing & Payroll first.',
            'Charges cannot be undone through this tool - use Remove Charges if needed.',
        ],
        tips: [
            'You can select multiple timesheets at once.',
            'Check "Replace Charges" in the modal to replace an existing charge instead of adding alongside it.',
            'Check the Execution History below to review previous uses of this tool.',
        ],
    },
    {
        ...getSupportTool('remove-charges'),
        adminToolId: 9,
        FormComponent: RemoveChargesForm,
        HistoryDetailsComponent: RemoveChargesHistoryDetails,
        examples: [
            {
                title: 'Remove an Incorrect Charge Type',
                scenario: 'A "Standby" charge was added to a timesheet by mistake and needs to be removed before billing.',
                steps: [
                    'Search for and select the timesheet number',
                    'The charges list will load automatically',
                    'Select the charge(s) to remove',
                    'Click Submit to remove the selected charges',
                ],
            },
            {
                title: 'Remove Multiple Charges',
                scenario: 'Several incorrect charge types were added to a timesheet during data entry.',
                steps: [
                    'Select the timesheet',
                    'Select all incorrect charges from the multi-select list',
                    'Submit to remove all selected charges at once',
                ],
            },
        ],
        warnings: [
            'Removed charges cannot be restored - verify the correct charges before submitting.',
            'This tool removes charge records from the timesheet permanently.',
            'If billing has already been approved, you may need to reject billing first.',
        ],
        tips: [
            'The charges list is disabled until a timesheet is selected.',
            'You can select multiple charges to remove at once.',
            'Check the Execution History below to review previous removals.',
        ],
    },
    {
        ...getSupportTool('sync-timesheet-contract-billing'),
        adminToolId: 8,
        FormComponent: SyncTimesheetContractBillingForm,
        HistoryDetailsComponent: SyncTimesheetContractBillingHistoryDetails,
        examples: [
            {
                title: 'Timesheet Not Showing in Billing',
                scenario: 'A timesheet was approved but is not appearing in the billing queue. Syncing with contract defaults should resolve it.',
                steps: [
                    'Enter the timesheet number',
                    'Select "Use Contract Defaults"',
                    'Click Submit to sync the timesheet with its contract billing settings',
                ],
            },
            {
                title: 'Force Timesheet to Billing',
                scenario: 'A timesheet needs to be moved to billing regardless of its current state.',
                steps: [
                    'Enter the timesheet number',
                    'Select "Move Timesheet to Billing"',
                    'Click Submit',
                ],
            },
        ],
        warnings: [
            'Forcing a timesheet to billing will override its current billing state.',
            'Use "Use Contract Defaults" first before trying the force options.',
            'Coordinate with the billing team before forcing billing status changes.',
        ],
        tips: [
            '"Use Contract Defaults" is the safest option and resolves most sync issues.',
            '"Move Timesheet to Billing: Charges" also syncs the individual charge billing status.',
            'Check the Execution History below to see previous uses of this tool.',
        ],
    },
    {
        ...getSupportTool('add-job-city'),
        adminToolId: 5,
        FormComponent: AddJobCityForm,
        HistoryDetailsComponent: AddJobCityHistoryDetails,
        examples: [
            {
                title: 'City Not Available in Job Setup',
                scenario: 'A dispatcher is creating a new job but the customer\'s city does not appear in the city dropdown.',
                steps: [
                    'Enter the city name exactly as it should appear',
                    'Select the state from the dropdown',
                    'Click Submit to add the city',
                    'The city will now be available in job setup dropdowns',
                ],
            },
        ],
        warnings: [
            'Verify the city name and state are correct before submitting - duplicates may cause confusion.',
            'City names are visible to all users in job setup forms.',
        ],
        tips: [
            'Use standard city name casing (e.g., "Los Angeles" not "LOS ANGELES").',
            'Check the Execution History to confirm the city was added successfully.',
        ],
    },
    {
        ...getSupportTool('import-customer-data'),
        adminToolId: 11,
        FormComponent: ImportCustomerDataForm,
        HistoryDetailsComponent: ImportCustomerDataHistoryDetails,
        examples: [
            {
                title: 'Onboard a New Tenant',
                scenario: 'A new tenant has been created and needs its customers and employees loaded from spreadsheets.',
                steps: [
                    'Select the target tenant',
                    'Select the import type (e.g., "Customers")',
                    'Attach the .csv file for that import type',
                    'Click Submit and review the Succeeded/Failed counts in the history',
                    'Repeat for each import type (Employees, Equipment, etc.)',
                ],
            },
            {
                title: 'Bulk-add Equipment',
                scenario: 'A customer provided a list of equipment that needs to be added in bulk.',
                steps: [
                    'Select the tenant',
                    'Select "Equipment" as the import type',
                    'Attach the equipment .csv',
                    'Submit and confirm the records imported',
                ],
            },
        ],
        warnings: [
            'Data is imported directly into the selected tenant - double-check the tenant before submitting.',
            'The CSV columns must match the format expected for the selected import type.',
            'Imports are not automatically reversible - verify the file before uploading.',
        ],
        tips: [
            'Import one data type at a time using the matching CSV.',
            'Review the Succeeded and Failed counts in the Execution History after each run.',
            'You can download the file that was uploaded for a previous run from the Execution History.',
        ],
    },
    {
        ...getSupportTool('rollback-payroll-export'),
        adminToolId: 19,
        FormComponent: RollbackPayrollExportForm,
        HistoryDetailsComponent: RollbackPayrollExportHistoryDetails,
        examples: [
            {
                title: 'Office Exported by Accident',
                scenario: 'An entire office was exported to payroll by mistake and needs to be recalculated before the pay period closes.',
                steps: [
                    'Search for and select the payroll export number',
                    'Select the office(s) that were exported by mistake',
                    'Select the employee(s) to roll back',
                    'Click Submit to return those payroll records to Processed status',
                ],
            },
            {
                title: 'Single Employee Needs Reprocessing',
                scenario: 'One employee in an export needs a correction and has to be pulled out and reprocessed on its own.',
                steps: [
                    'Search for and select the payroll export number',
                    'Select the office the employee belongs to',
                    'Select only that employee from the employee list',
                    'Submit to roll back just that employee\'s payroll records',
                ],
            },
        ],
        warnings: [
            'This only resets the selected Payroll records - the parent timesheet status is not changed.',
            'Coordinate with the payroll team before rolling back an export that may already be with a third-party payroll provider.',
        ],
        tips: [
            'Offices and employees are scoped to the selected export - only records that were actually part of that export will appear.',
            'You can select multiple offices and employees at once.',
            'Check the Execution History below to review previous rollbacks.',
        ],
    },
    {
        ...getSupportTool('crew-eta-test-drive'),
        adminToolId: 20,
        FormComponent: CrewEtaTestDriveForm,
        HistoryDetailsComponent: CrewEtaTestDriveHistoryDetails,
        examples: [
            {
                title: 'QA Functional Pass',
                scenario: 'QA needs to verify the customer-portal crew ETA (chip, route line, live movement, arrival) without a live GPS-tracked truck.',
                steps: [
                    'Pick the demo customer and a dispatch office',
                    'Enter the meeting address coordinates (any real place works)',
                    'Set the drive duration to 15 minutes and submit',
                    'Open the returned portal link as the demo customer\'s portal contact',
                    'Watch the ETA chip, route line, and truck marker through arrival',
                    'Use "End test drive" when finished so the demo job is cleaned up',
                ],
            },
            {
                title: 'Sales Demo',
                scenario: 'Sales wants to show a prospect the Uber-style crew tracking on their own site address.',
                steps: [
                    'Pick the demo customer and enter the prospect\'s address coordinates',
                    'Set the drive duration to about 4 minutes and submit',
                    'Open the portal link on a phone as the demo portal contact',
                    'Re-submit the form to restart the drive for the next run',
                ],
            },
        ],
        warnings: [
            'The tool works in EVERY environment - there is no server-side environment gate. Never run it in production: it creates real data in the selected tenant.',
            'It creates a real job, timesheet, SIM vehicle, and sim employee in the selected tenant, visible in dispatch and equipment views until the test drive is ended.',
            'Always use a dedicated demo customer, never a real customer.',
            'The truck follows the route visually but its positions are synthetic - do not present it as a live vehicle.',
        ],
        tips: [
            'Re-submitting for the same customer on the same day restarts the existing drive instead of creating another job.',
            'One reusable SIM vehicle is kept per dispatch office - test drives never accumulate vehicles.',
            'Locally (no Google key) the route line is a straight-line placeholder; on the dev App Service it follows real roads.',
        ],
    },
    {
        // Standalone: no adminToolId, so no AdminTool row and no execution history — the result
        // line is the record. Listed on the Support Tools page only (Craig, 2026-09-04).
        ...getSupportTool('test-messaging'),
        FormComponent: TestMessagingForm,
        examples: [
            {
                title: 'Prove messaging on a new environment',
                scenario: 'An environment was just stood up and its Twilio and SendGrid values were seeded; nobody has received a message from it yet.',
                steps: [
                    'Enter your own mobile number and press Send SMS',
                    'Enter your own email address and press Send Email',
                    'Confirm both arrive and note the from-number and from-address shown',
                ],
            },
        ],
        tips: ['The message body is "Test from <this site\'s host name>", so you can tell which environment sent it.'],
    },
];

export const SupportToolAdminToolIds = SupportToolRegistry
    .map((tool) => tool.adminToolId)
    .filter(Boolean);

// Tools with no AdminTool row: listed from the registry alone, never from the AdminTool search.
export const StandaloneSupportTools = SupportToolRegistry.filter((tool) => !tool.adminToolId);

export const getSupportToolRegistryItem = (value, field = 'id') => (
    SupportToolRegistry.find((tool) => tool[field] === value) ?? null
);
